import React, { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  getPipelineEmailRecipients,
  previewPipelineEmail,
  sendPipelineEmail,
  type PipelineEmailBucket,
  type PipelineEmailPreview,
  type SendPipelineEmailResult,
} from '../api/plantersService';

function axiosErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    return typeof msg === 'string' ? msg : err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

interface StageEmailModalProps {
  bucket: PipelineEmailBucket;
  label: string;
  isOpen: boolean;
  onClose: () => void;
}

const StageEmailModal: React.FC<StageEmailModalProps> = ({ bucket, label, isOpen, onClose }) => {
  const titleId = useId();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [excludedIds, setExcludedIds] = useState<Set<number>>(() => new Set());
  const [showRecipients, setShowRecipients] = useState(false);
  const [preview, setPreview] = useState<PipelineEmailPreview | null>(null);
  const [result, setResult] = useState<SendPipelineEmailResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recipientsQuery = useQuery({
    queryKey: ['pipelineEmailRecipients', bucket],
    queryFn: () => getPipelineEmailRecipients(bucket),
    enabled: isOpen,
    staleTime: 30_000,
  });

  const recipients = useMemo(() => recipientsQuery.data?.recipients ?? [], [recipientsQuery.data]);
  const selectedIds = useMemo(
    () => recipients.filter((r) => !excludedIds.has(r.id)).map((r) => r.id),
    [recipients, excludedIds],
  );
  const withoutEmailCount = recipientsQuery.data?.withoutEmailCount ?? 0;

  const sampleName = useMemo(
    () => recipients.find((r) => !excludedIds.has(r.id))?.name ?? '',
    [recipients, excludedIds],
  );

  const sendMutation = useMutation({
    mutationFn: () =>
      sendPipelineEmail({
        bucket,
        subject: subject.trim(),
        message,
        planterIds: selectedIds,
      }),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
    },
    onError: (e) => {
      setResult(null);
      setError(axiosErrorMessage(e));
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => previewPipelineEmail({ subject: subject.trim(), message, sampleName }),
    onSuccess: (data) => {
      setError(null);
      setPreview(data);
    },
    onError: (e) => {
      setError(axiosErrorMessage(e));
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    setSubject('');
    setMessage('');
    setExcludedIds(new Set());
    setShowRecipients(false);
    setPreview(null);
    setResult(null);
    setError(null);
  }, [isOpen, bucket]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleExclude = (id: number) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const composeValid = subject.trim().length > 0 && message.trim().length > 0;
  const canSend = composeValid && selectedIds.length > 0 && !sendMutation.isPending;
  const canPreview = composeValid && !previewMutation.isPending;

  const onSend = () => {
    setError(null);
    const ok = window.confirm(
      `Send this email to ${selectedIds.length} planter${selectedIds.length === 1 ? '' : 's'} in ${label}?`,
    );
    if (!ok) return;
    sendMutation.mutate();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-[101] w-full max-w-lg max-h-[88vh] flex flex-col rounded-xl bg-white shadow-xl border border-gray-200"
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div>
            <h3 id={titleId} className="text-base font-semibold text-gray-900">
              Email {label} planters
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {recipientsQuery.isLoading
                ? 'Loading recipients…'
                : `${selectedIds.length} of ${recipients.length} selected`}
              {withoutEmailCount > 0 ? ` · ${withoutEmailCount} without an email` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4 space-y-4">
          {recipientsQuery.isError && (
            <p className="text-sm text-red-700">Could not load recipients. Try again.</p>
          )}

          {result ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                <p className="font-medium">
                  Sent to {result.sent} of {result.total} planter{result.total === 1 ? '' : 's'}.
                </p>
                {result.failed > 0 ? (
                  <p className="mt-1 text-amber-800">
                    {result.failed} message{result.failed === 1 ? '' : 's'} could not be delivered.
                  </p>
                ) : null}
              </div>
              {result.failures.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5">
                  <p className="text-xs font-medium text-amber-900 mb-1.5">Failed recipients</p>
                  <ul className="max-h-48 overflow-y-auto space-y-1 pr-1">
                    {result.failures.map((f, i) => (
                      <li key={`${f.email}-${i}`} className="text-xs text-amber-900">
                        <span className="font-medium">{f.name || f.email || 'Unknown'}</span>
                        {f.name && f.email ? <span className="text-amber-700"> ({f.email})</span> : null}
                        <span className="block text-amber-700">{f.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                <span className="text-xs font-medium text-gray-500">Subject</span>
                <div className="font-medium text-gray-900 break-words">
                  {preview.subject || <span className="text-gray-400">(no subject)</span>}
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">
                  Preview{sampleName ? ` (personalized for ${sampleName.split(' ')[0]})` : ''}
                </span>
                <iframe
                  title="Email preview"
                  srcDoc={preview.html}
                  sandbox=""
                  className="mt-1 w-full h-72 rounded-md border border-gray-200 bg-white"
                />
              </div>
            </div>
          ) : (
            <>
              <div>
                <label htmlFor={`${titleId}-subject`} className="block text-xs font-medium text-gray-600 mb-1">
                  Subject
                </label>
                <input
                  id={`${titleId}-subject`}
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject line"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label htmlFor={`${titleId}-message`} className="block text-xs font-medium text-gray-600 mb-1">
                  Message
                </label>
                <textarea
                  id={`${titleId}-message`}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={8}
                  placeholder={'Write your message…\n\nTip: {{firstName}} inserts each planter’s first name.'}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Plain text. Replies go to your account email. Use <code>{'{{firstName}}'}</code> for personalization.
                </p>
              </div>

              <div className="border-t border-gray-100 pt-3">
                <button
                  type="button"
                  onClick={() => setShowRecipients((v) => !v)}
                  className="text-xs font-medium text-primary-700 hover:text-primary-900"
                >
                  {showRecipients ? 'Hide recipients' : `Review recipients (${recipients.length})`}
                </button>
                {showRecipients && (
                  <ul className="mt-2 max-h-56 overflow-y-auto space-y-1 pr-1">
                    {recipients.length === 0 && !recipientsQuery.isLoading ? (
                      <li className="text-xs text-gray-500">No planters with an email in this stage.</li>
                    ) : null}
                    {recipients.map((r) => {
                      const included = !excludedIds.has(r.id);
                      return (
                        <li
                          key={r.id}
                          className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50/80 px-2 py-1.5"
                        >
                          <input
                            type="checkbox"
                            checked={included}
                            onChange={() => toggleExclude(r.id)}
                            className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">{r.name}</div>
                            <div className="truncate text-xs text-gray-500">{r.email}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}

          {error && !result ? <p className="text-sm text-red-700">{error}</p> : null}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={result ? onClose : preview ? () => setPreview(null) : onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {result ? 'Close' : preview ? 'Back to edit' : 'Cancel'}
          </button>
          {!result && !preview && (
            <button
              type="button"
              onClick={() => previewMutation.mutate()}
              disabled={!canPreview}
              className="rounded-md border border-primary-300 bg-white px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {previewMutation.isPending ? 'Rendering…' : 'Preview'}
            </button>
          )}
          {!result && (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50"
            >
              {sendMutation.isPending
                ? 'Sending…'
                : `Send to ${selectedIds.length} planter${selectedIds.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default StageEmailModal;
